import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import IconButton from "@/components/ui/icon-button";
import { DecimalInput } from "@/components/ui/decimal-input";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Trash2, EllipsisVertical } from "lucide-react";
import { cn } from "@/lib/utils";
import type { LineItem } from "document-api-client";

export interface LineItemCardProps {
    item: LineItem;
    index: number;
    isReadOnly: boolean;
    useMixedTaxRates: boolean;
    onLineItemChange: <K extends keyof LineItem>(
        index: number,
        field: K,
        value: LineItem[K],
    ) => void;
    onDelete: (index: number) => void;
}

export function LineItemCard({
    item,
    index,
    isReadOnly,
    useMixedTaxRates,
    onLineItemChange,
    onDelete,
}: LineItemCardProps) {
    return (
        <Card className="p-3 gap-3 flex-1 bg-md-surface-level-2 pl-1">
            <div className={cn(isReadOnly ? "" : "grid grid-cols-[38px_1fr]")}>
                {!isReadOnly && (
                    <div className="flex flex-col justify-center items-start">
                        <DropdownMenu>
                            <DropdownMenuTrigger className="--translate-y-2">
                                <IconButton
                                    variant="ghost"
                                    color="neutral"
                                    size="sm"
                                >
                                    <EllipsisVertical className="h-4 w-4" />
                                </IconButton>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start">
                                <DropdownMenuItem
                                    onClick={() => onDelete(index)}
                                    variant="destructive"
                                >
                                    <Trash2 className="mr-2 h-4 w-4" />
                                    Delete
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                )}
                <div className="flex flex-col gap-2">
                    {/* Item name row with product code */}
                    <div className="flex justify-between items-end gap-2">
                        <div className="w-3/4">
                            <label className="text-xs text-gray-500 block mb-1">
                                Item {index + 1}
                            </label>
                            <Input
                                value={item.item || ""}
                                onChange={(e) =>
                                    onLineItemChange(
                                        index,
                                        "item",
                                        e.target.value,
                                    )
                                }
                                readOnly={isReadOnly}
                                className={`text-sm font-medium ${isReadOnly ? "bg-gray-50" : ""}`}
                                placeholder="Item name"
                            />
                        </div>
                        <div className="w-1/4">
                            <label className="text-xs text-gray-500 block mb-1">
                                Product Code
                            </label>
                            <Input
                                value={item.productCode || ""}
                                onChange={(e) =>
                                    onLineItemChange(
                                        index,
                                        "productCode",
                                        e.target.value,
                                    )
                                }
                                readOnly={isReadOnly}
                                className={`text-sm ${isReadOnly ? "bg-gray-50" : ""}`}
                            />
                        </div>
                    </div>

                    {/* Quantity, Unit Price, Price, Tax Rate in one row on larger screens */}
                    <div className="flex justify-between items-end gap-2">
                        <div className="flex-1">
                            <label className="text-xs text-gray-500 block mb-1">
                                Quantity
                            </label>
                            <DecimalInput
                                value={item.quantity}
                                onChange={(value) =>
                                    onLineItemChange(index, "quantity", value)
                                }
                                readOnly={isReadOnly}
                                className="text-sm"
                                step={1}
                            />
                        </div>
                        <div className="flex-1">
                            <label className="text-xs text-gray-500 block mb-1">
                                Unit Price
                            </label>
                            <DecimalInput
                                value={item.unitPrice}
                                onChange={(value) =>
                                    onLineItemChange(index, "unitPrice", value)
                                }
                                readOnly={isReadOnly}
                                className="text-sm text-right"
                            />
                        </div>
                        {useMixedTaxRates && (
                            <div className="flex-1">
                                <label className="text-xs text-gray-500 block mb-1">
                                    Tax Rate (%)
                                </label>
                                <DecimalInput
                                    value={item.taxRate}
                                    onChange={(value) =>
                                        onLineItemChange(
                                            index,
                                            "taxRate",
                                            value,
                                        )
                                    }
                                    readOnly={isReadOnly || !useMixedTaxRates}
                                    className="text-sm text-right"
                                />
                            </div>
                        )}
                        <div className="flex-1">
                            <label className="text-xs text-gray-500 block mb-1">
                                Price
                            </label>
                            <DecimalInput
                                value={item.price}
                                onChange={(value) =>
                                    onLineItemChange(index, "price", value)
                                }
                                readOnly={isReadOnly}
                                className="text-sm text-right"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </Card>
    );
}
